import Tooltip from 'rc-tooltip';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LootboxItem } from '@/api/common';
import { Button } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { useLootboxBonus } from '@/modules/promotions';
import { PromotionPageTabs } from '@/modules/promotions/components/PromotionsPage';
import { formatLootboxItemTitle } from '../../../../helpers/fortuneWheel';
import {
  overlayInnerStyle,
  overlayStyle,
  ScPrizeWheel,
  ScTermsButton,
  ScTermsSection,
  ScWinningPrize,
} from './styled';

interface PrizeWheelProps {
  lootboxItem: LootboxItem;
}

export const PrizeWheel: FC<PrizeWheelProps> = ({ lootboxItem }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('wheel');

  const [isOpenTerms, setIsOpenTerms] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const winningPrizeRef = useRef<HTMLDivElement | null>(null);
  const claimButtonRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = () => {
    setIsTooltipVisible(false);
  };

  const bonus = useMemo(() => lootboxItem.bonuses[0], [lootboxItem]);

  const { wager, bonusAmount, maxWin } = useLootboxBonus(bonus);

  useEffect(() => {
    if (isOpenTerms) {
      const claimButton = claimButtonRef.current;
      const winningPrize = winningPrizeRef.current;

      if (claimButton && winningPrize) {
        const timeoutId = setTimeout(() => {
          const elementTop = claimButton.offsetTop;
          winningPrize.scrollTo({
            top: elementTop,
            behavior: 'smooth',
          });
        }, 210);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [isOpenTerms]);

  return (
    <ScPrizeWheel>
      <ScWinningPrize
        isOpen={isOpenTerms}
        onScroll={handleScroll}
        ref={winningPrizeRef}
      >
        <div className={`inside ${language}`}>
          <p className="congrats">{t('congrats')}</p>
          <p className="description">{t('subCongrats')}</p>

          <div className="prize-info">
            <p className="prize">{formatLootboxItemTitle(bonus.title)}</p>
            {maxWin && (
              <Tooltip
                onVisibleChange={(event) => setIsTooltipVisible(event)}
                overlay={maxWin}
                overlayInnerStyle={overlayInnerStyle}
                overlayStyle={overlayStyle}
                placement="top"
                showArrow={false}
                visible={isTooltipVisible}
              >
                <div className="info">
                  <IconComponent name="tooltipIcon" />
                </div>
              </Tooltip>
            )}
          </div>

          <div className="btn-container" ref={claimButtonRef}>
            <Button
              gold
              large
              to={`/promotions?tab=${PromotionPageTabs.ACTIVE_BONUSES}`}
              wide
            >
              {t('claimNow')}
            </Button>
          </div>

          <ScTermsSection isOpen={isOpenTerms}>
            <div className="btn_wrapper">
              <ScTermsButton
                isOpen={isOpenTerms}
                onClick={() => setIsOpenTerms(!isOpenTerms)}
              >
                <span>{t('common:button.termsAndConditions')}</span>
                <IconComponent name="whiteArrowRight" size="14px" />
              </ScTermsButton>
            </div>

            <div className="content-box">
              <div className="content">
                <p className="bonus">
                  {t('termsAndConditions.bonus', {
                    bonus: formatLootboxItemTitle(bonus.title),
                  })}
                </p>
                <p>{t('termsAndConditions.expiresIn')}</p>
                <p>{t('termsAndConditions.wager', { wager })}</p>
                {bonusAmount && (
                  <p>{t('termsAndConditions.maxBonus', { bonusAmount })}</p>
                )}
                {maxWin && <p>{t('termsAndConditions.maxWin', { maxWin })}</p>}
              </div>
            </div>
          </ScTermsSection>
        </div>
      </ScWinningPrize>
    </ScPrizeWheel>
  );
};
